import { cloneElement, isValidElement, type ReactElement } from "react";

import type { NavLink, NavLinkBody } from "./navDrawer.types";

/**
 * Marks drawer icons as decorative: adds a11y attributes and a className slug for tests.
 * Autoparts icons render SVGs but often omit those props from their TS types.
 */
function cloneNavIconSvg(
  icon: ReactElement,
  variant: "alt" | "default",
  labelText: string
): ReactElement {
  const normalizedName = labelText.trim().toLowerCase().replace(/\s+/g, "-");
  const iconClass = `${variant === "alt" ? "alternative-" : ""}${normalizedName}-icon`;

  const priorProps: Record<string, unknown> =
    typeof icon.props === "object" && icon.props !== null
      ? { ...(icon.props as Record<string, unknown>) }
      : {};

  return cloneElement(icon, {
    ...priorProps,
    className: iconClass,
    "aria-hidden": "true",
    focusable: "false",
    role: "presentation"
  } as Parameters<typeof cloneElement>[1]);
}

export function createNavDrawerDefaultIconRenderer(
  renderIcon: () => ReactElement,
  labelText: string
): () => ReactElement {
  return () => {
    try {
      const el = renderIcon();
      if (!isValidElement(el)) {
        throw new Error(
          `Expected ReactElement from nav icon for "${labelText}"`
        );
      }
      return cloneNavIconSvg(el, "default", labelText);
    } catch (error) {
      console.error("createNavDrawerDefaultIconRenderer:", error);
      return renderIcon();
    }
  };
}

export function createNavDrawerAltIconRenderer(
  renderIcon: (fillColor: string) => ReactElement,
  labelText: string
): (fillColor: string) => ReactElement {
  return (fillColor: string) => {
    try {
      const el = renderIcon(fillColor);
      if (!isValidElement(el)) {
        throw new Error(
          `Expected ReactElement from nav alt icon for "${labelText}"`
        );
      }
      return cloneNavIconSvg(el, "alt", labelText);
    } catch (error) {
      console.error("createNavDrawerAltIconRenderer:", error);
      return renderIcon(fillColor);
    }
  };
}

export function decorateNavDrawerLinkIcons(
  body: NavLinkBody
): Omit<NavLink, "entryType"> {
  return {
    text: body.text,
    to: body.to,
    additionalRoutes: body.additionalRoutes,
    icon: createNavDrawerDefaultIconRenderer(body.icon, body.text),
    altIcon: createNavDrawerAltIconRenderer(body.altIcon, body.text)
  };
}
